import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * useScans — paginated fetch of scan_logs.
 *
 * Endpoint: GET /api/dashboard/scans?limit=50&before=ISO&result=X&staff_id=X
 *
 * Cursor-based pagination via `before` timestamp. When the UI wants the
 * next page, it calls fetchMore() which uses the last-seen scanned_at
 * as the cursor. Simpler than offset-based — doesn't skip rows when new
 * scans land, stable under real-world concurrent writes.
 */
export function useScans(filters = {}) {
  const [state, setState] = useState({
    scans: [],
    hasMore: false,
    loading: true,
    loadingMore: false,
    error: null,
    cursor: null, // ISO timestamp of the oldest scan we've seen
  });

  const filterKey = JSON.stringify(filters);

  const loadInitial = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null, scans: [], cursor: null }));
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (filters.result) params.set('result', filters.result);
      if (filters.staff_id) params.set('staff_id', filters.staff_id);
      const data = await api.get(`/api/dashboard/scans?${params.toString()}`);
      setState({
        scans: data.scans || [],
        hasMore: !!data.has_more,
        loading: false,
        loadingMore: false,
        error: null,
        cursor: data.next_before || null,
      });
    } catch (e) {
      setState(s => ({ ...s, loading: false, loadingMore: false, error: e }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const loadMore = useCallback(async () => {
    setState(s => ({ ...s, loadingMore: true }));
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('before', state.cursor);
      if (filters.result) params.set('result', filters.result);
      if (filters.staff_id) params.set('staff_id', filters.staff_id);
      const data = await api.get(`/api/dashboard/scans?${params.toString()}`);
      setState(s => ({
        scans: [...s.scans, ...(data.scans || [])],
        hasMore: !!data.has_more,
        loading: false,
        loadingMore: false,
        error: null,
        cursor: data.next_before || null,
      }));
    } catch (e) {
      setState(s => ({ ...s, loadingMore: false, error: e }));
    }
  }, [state.cursor, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  return { ...state, refresh: loadInitial, loadMore };
}

/**
 * useScanStats — summary stats for the scans dashboard.
 *
 * Endpoint: GET /api/dashboard/scans/stats
 * Returns: { total, last_24h, last_7d, last_30d, unique_staff, top_staff[], by_result }
 *
 * (Post-Patch-1+2, these are all the fields Randy returns.)
 */
export function useScanStats() {
  const [state, setState] = useState({
    stats: null,
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get('/api/dashboard/scans/stats');
      setState({ stats: data, loading: false, error: null });
    } catch (e) {
      setState({ stats: null, loading: false, error: e });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}
